import React, { useState, useEffect, useRef } from 'react';
import { API } from '../lib/api';

const DEFAULT_WELCOME = {
  role: 'assistant',
  text: "Hey! 👋 I'm **Helper**, your personal BORDS assistant.\n\nWhether you need to track an order, find your oversized fit, check our 240 GSM fabric specs, or explore the latest drops, I'm here for you!\n\nTap a quick topic below or type your question:"
};

const QUICK_PROMPTS = [
  { label: '📦 Track Order', text: 'How do I track my order?' },
  { label: '📏 Size Guide', text: 'What is the sizing and fit guide for 240 GSM?' },
  { label: '👕 Available Tees', text: 'Which products are currently available in the store?' },
  { label: '🚚 Shipping Time', text: 'How long does shipping take?' },
  { label: '↩ 7-Day Returns', text: 'What is your return and exchange policy?' },
  { label: '🙋 Human Support', text: 'How can I connect with human support or get help with my order?' },
];

export default function HelperChat({ storeSettings = {} }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem('bords_helper_messages');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_) {}
    return [DEFAULT_WELCOME];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Listen for custom event to open helper from anywhere (footer links, buttons)
  useEffect(() => {
    const handleOpenHelper = () => setIsOpen(true);
    window.addEventListener('bords_open_helper', handleOpenHelper);
    return () => window.removeEventListener('bords_open_helper', handleOpenHelper);
  }, []);

  // Sync messages to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('bords_helper_messages', JSON.stringify(messages));
    } catch (_) {}
  }, [messages]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  const handleSend = async (customText) => {
    const textToSend = (customText || input || '').trim();
    if (!textToSend || isLoading) return;

    const userMessage = { role: 'user', text: textToSend };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');
    setIsLoading(true);

    try {
      // Build conversation history for context
      const history = nextMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role === 'user' ? 'user' : 'model', text: m.text }));

      const res = await fetch(`${API}/helper/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: textToSend, history })
      });

      if (!res.ok) throw new Error('Helper service offline');
      const data = await res.json();
      const replyText = data.reply || "I'm having trouble connecting right now. Please email our team at bordtrynew@gmail.com!";

      setMessages(prev => [...prev, { role: 'assistant', text: replyText }]);
    } catch (err) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          text: "I couldn't reach the server right now. You can leave your question and Order ID right here, or contact our team directly at **bordtrynew@gmail.com**!"
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    const fresh = [DEFAULT_WELCOME];
    setMessages(fresh);
    try { sessionStorage.setItem('bords_helper_messages', JSON.stringify(fresh)); } catch (_) {}
  };

  // Simple Markdown parser for bold (**text**), bullet points, and URLs
  const renderFormattedText = (rawText) => {
    if (!rawText) return null;
    const lines = rawText.split('\n');

    return lines.map((line, lineIdx) => {
      // Replace URLs with clickable anchors
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = line.split(urlRegex);

      const renderedLine = parts.map((part, pIdx) => {
        if (part.match(urlRegex)) {
          return (
            <a
              key={pIdx}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#c8471a', textDecoration: 'underline', wordBreak: 'break-all', fontWeight: 600 }}
            >
              {part}
            </a>
          );
        }

        // Bold formatting **text**
        const boldRegex = /\*\*(.*?)\*\*/g;
        const subParts = part.split(boldRegex);
        return subParts.map((sub, sIdx) => {
          if (sIdx % 2 === 1) {
            return <strong key={sIdx}>{sub}</strong>;
          }
          return sub;
        });
      });

      return (
        <span key={lineIdx} style={{ display: 'block', minHeight: line.trim() ? 'auto' : '8px', marginBottom: 3 }}>
          {renderedLine}
        </span>
      );
    });
  };

  return (
    <>
      {/* ── Floating Launcher Trigger ── */}
      <button
        type="button"
        id="bords-helper-trigger"
        aria-label="Open Helper AI Chat"
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 999999,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '9px',
          background: '#111111',
          color: '#FFFFFF',
          border: '1px solid rgba(255,255,255,0.22)',
          borderRadius: '999px',
          padding: '10px 18px',
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '13.5px',
          fontWeight: 600,
          letterSpacing: '0.02em',
          cursor: 'pointer',
          boxShadow: '0 6px 22px rgba(0,0,0,0.28)',
          transition: 'transform 0.22s ease, box-shadow 0.22s ease, background 0.2s ease',
          userSelect: 'none'
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; }}
      >
        {/* Animated Bot Sparkle Avatar */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #c8471a, #D4FF3F)',
            color: '#111',
            fontSize: '11px',
            fontWeight: 800,
            flexShrink: 0
          }}
        >
          ⚡
        </span>
        <span>Helper</span>
        {/* Pulsing Green Live Dot */}
        <span
          style={{
            width: '7px',
            height: '7px',
            borderRadius: '50%',
            background: '#2ecc71',
            boxShadow: '0 0 6px #2ecc71',
            marginLeft: '-2px'
          }}
        />
        {unreadCount > 0 && (
          <span
            style={{
              background: '#c8471a',
              color: '#fff',
              fontSize: '10px',
              padding: '1px 5px',
              borderRadius: '10px',
              fontWeight: 700
            }}
          >
            {unreadCount}
          </span>
        )}
      </button>

      {/* ── Helper Chat Window ── */}
      {isOpen && (
        <div
          id="bords-helper-window"
          role="dialog"
          aria-label="Helper AI Assistant"
          style={{
            position: 'fixed',
            bottom: '84px',
            right: '24px',
            width: '390px',
            maxWidth: 'calc(100vw - 32px)',
            height: '560px',
            maxHeight: 'calc(100vh - 120px)',
            background: 'var(--bord-surface, #FFFFFF)',
            color: 'var(--bord-fg, #111111)',
            borderRadius: '16px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.22), 0 2px 10px rgba(0,0,0,0.08)',
            border: '1px solid rgba(128,128,128,0.2)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9999999,
            overflow: 'hidden',
            animation: 'bordsHelperPop 0.24s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 18px',
              background: '#111111',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #c8471a, #D4FF3F)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#111',
                  fontWeight: 900,
                  fontSize: '14px'
                }}
              >
                H
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>Helper</span>
                  <span
                    style={{
                      fontSize: '9px',
                      textTransform: 'uppercase',
                      padding: '2px 5px',
                      borderRadius: '4px',
                      background: 'rgba(212,255,63,0.25)',
                      color: '#D4FF3F',
                      fontWeight: 800,
                      letterSpacing: '0.05em'
                    }}
                  >
                    AI Bot
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: '#999', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2ecc71', display: 'inline-block' }} />
                  BORDS Instant Support
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                onClick={handleReset}
                title="Reset conversation"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#AAA',
                  cursor: 'pointer',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              >
                ↺
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                title="Close chat"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '18px',
                  lineHeight: 1
                }}
              >
                &times;
              </button>
            </div>
          </div>

          {/* Messages Body */}
          <div
            style={{
              flex: 1,
              padding: '16px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              background: 'var(--bord-bg, #FAF9F6)'
            }}
          >
            {messages.map((m, idx) => {
              const isUser = m.role === 'user';
              return (
                <div
                  key={idx}
                  style={{
                    alignSelf: isUser ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isUser ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: isUser ? '16px 16px 3px 16px' : '16px 16px 16px 3px',
                      background: isUser ? '#111111' : 'var(--bord-surface, #FFFFFF)',
                      color: isUser ? '#FFFFFF' : 'var(--bord-fg, #111111)',
                      fontSize: '13px',
                      lineHeight: 1.55,
                      boxShadow: isUser ? '0 2px 8px rgba(0,0,0,0.18)' : '0 2px 8px rgba(0,0,0,0.06)',
                      border: isUser ? 'none' : '1px solid rgba(0,0,0,0.08)',
                      wordBreak: 'break-word'
                    }}
                  >
                    {renderFormattedText(m.text)}
                  </div>
                </div>
              );
            })}

            {isLoading && (
              <div
                style={{
                  alignSelf: 'flex-start',
                  padding: '10px 16px',
                  borderRadius: '16px 16px 16px 3px',
                  background: 'var(--bord-surface, #FFFFFF)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span className="helper-pulse-dot" style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#c8471a' }} />
                <span className="helper-pulse-dot" style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#c8471a', animationDelay: '0.2s' }} />
                <span className="helper-pulse-dot" style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#c8471a', animationDelay: '0.4s' }} />
                <span style={{ fontSize: '11px', color: '#888', marginLeft: '4px' }}>Helper is typing...</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Action Chips */}
          <div
            style={{
              padding: '8px 12px',
              background: 'var(--bord-surface, #FFFFFF)',
              borderTop: '1px solid rgba(0,0,0,0.06)',
              display: 'flex',
              gap: '6px',
              overflowX: 'auto',
              whiteSpace: 'nowrap'
            }}
          >
            {QUICK_PROMPTS.map((q, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSend(q.text)}
                style={{
                  background: 'rgba(0,0,0,0.05)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  color: 'inherit',
                  borderRadius: '12px',
                  padding: '5px 10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'background 0.15s ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(200,71,26,0.1)'; e.currentTarget.style.borderColor = '#c8471a'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'; }}
              >
                {q.label}
              </button>
            ))}
          </div>

          {/* Input Box & In-App Support Footer */}
          <div
            style={{
              padding: '12px 14px',
              background: 'var(--bord-surface, #FFFFFF)',
              borderTop: '1px solid rgba(0,0,0,0.08)'
            }}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Ask Helper about orders, sizing, fabric..."
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '24px',
                  border: '1px solid rgba(0,0,0,0.15)',
                  background: 'var(--bord-bg, #FAF9F6)',
                  color: 'inherit',
                  fontSize: '13px',
                  outline: 'none'
                }}
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                aria-label="Send message"
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: input.trim() && !isLoading ? '#111111' : '#CCCCCC',
                  color: '#FFFFFF',
                  border: 'none',
                  cursor: input.trim() && !isLoading ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'background 0.2s ease'
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </form>

            <div
              style={{
                marginTop: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
                color: '#888'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2ecc71', display: 'inline-block' }} />
                In-App AI Assistant
              </span>
              <a
                href="mailto:bordtrynew@gmail.com"
                style={{ color: '#c8471a', textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
              >
                <span>bordtrynew@gmail.com</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Embedded Animations & Positioning Responsive Overrides */}
      <style>{`
        @keyframes bordsHelperPop {
          from { opacity: 0; transform: scale(0.95) translateY(12px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes helperDotPulse {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
        .helper-pulse-dot {
          animation: helperDotPulse 1.2s infinite ease-in-out;
        }

        @media (max-width: 900px) {
          #bords-helper-trigger {
            bottom: 16px !important;
            right: 14px !important;
            padding: 8px 14px !important;
            font-size: 12.5px !important;
          }
          body.has-bottom-cta #bords-helper-trigger {
            bottom: calc(88px + env(safe-area-inset-bottom, 0px)) !important;
            transform: translateY(-68px) !important;
          }
          body.bords-menu-open #bords-helper-trigger {
            bottom: calc(148px + env(safe-area-inset-bottom, 0px)) !important;
            transform: translateY(-130px) !important;
          }
          #bords-helper-window {
            bottom: 70px !important;
            right: 12px !important;
            width: calc(100vw - 24px) !important;
            height: calc(100vh - 140px) !important;
          }
        }
      `}</style>
    </>
  );
}
